import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  HelpCircle,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  Eye,
  Filter,
  BookOpen,
  Award,
  BarChart3,
  Calendar,
  ArrowLeft,
} from "lucide-react";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyStudentProfile } from "../../services/students";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { quizService } from "../../services/quizService";

const StudentQuizzes: React.FC = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);

  const { data: studentData, isLoading: studentLoading } = useQuery({
    queryKey: ["student-profile"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  // Fetch quizzes
  const {
    data: quizzes = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student-quizzes"],
    queryFn: () => quizService.getStudentQuizzes(),
  });

  // Filter quizzes based on search and status
  const filteredQuizzes = quizzes.filter((quiz) => {
    // Hide e-course quizzes on this page
    if ((quiz as any).courseType === "ecourse") {
      return false;
    }

    const courseTitle =
      typeof (quiz as any).courseId === "object"
        ? (quiz as any).courseId?.title?.toLowerCase() ||
          (quiz as any).courseId?.name?.toLowerCase() ||
          ""
        : "";
    const matchesSearch =
      quiz.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      courseTitle.includes(searchTerm.toLowerCase());

    const matchesStatus =
      !statusFilter || (quiz as any).status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "text-white bg-gradient-to-r from-blue-500 to-indigo-500";
      case "in_progress":
        return "text-white bg-gradient-to-r from-yellow-500 to-orange-500";
      case "completed":
        return "text-white bg-gradient-to-r from-green-500 to-emerald-500";
      case "overdue":
        return "text-white bg-gradient-to-r from-red-500 to-pink-500";
      case "expired":
        return "text-white bg-gradient-to-r from-slate-500 to-slate-600";
      case "unavailable":
        return "text-white bg-gradient-to-r from-orange-500 to-amber-500";
      default:
        return "text-white bg-gradient-to-r from-slate-500 to-slate-600";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "available":
        return <Play className="w-4 h-4" />;
      case "in_progress":
        return <Clock className="w-4 h-4" />;
      case "completed":
        return <CheckCircle className="w-4 h-4" />;
      case "overdue":
        return <AlertCircle className="w-4 h-4" />;
      case "expired":
        return <AlertCircle className="w-4 h-4" />;
      case "unavailable":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <HelpCircle className="w-4 h-4" />;
    }
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 90) return "text-green-600";
    if (percentage >= 80) return "text-blue-600";
    if (percentage >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  const formatDueDate = (dueDate?: string) => {
    if (!dueDate) return null;

    const date = new Date(dueDate);
    const now = new Date();
    const diffInHours = (date.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 0) {
      return "Overdue";
    } else if (diffInHours < 24) {
      return `Due in ${Math.round(diffInHours)} hours`;
    } else {
      const diffInDays = Math.round(diffInHours / 24);
      return `Due in ${diffInDays} day${diffInDays !== 1 ? "s" : ""}`;
    }
  };

  const canTakeQuiz = (quiz: any) => {
    return (
      quiz.status === "available" &&
      quiz.isAvailable &&
      quiz.attempts < (quiz.maxAttempts || 1)
    );
  };

  const getActionButton = (quiz: any) => {
    if (quiz.status === "in_progress") {
      return (
        <button
          onClick={() => navigate(`/student/elearning/quizzes/${quiz._id}`)}
          className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-5 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 font-semibold"
        >
          <Play className="w-4 h-4" />
          Continue Quiz
        </button>
      );
    }

    if (canTakeQuiz(quiz)) {
      return (
        <button
          onClick={() => navigate(`/student/elearning/quizzes/${quiz._id}`)}
          className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-5 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 font-semibold"
        >
          <Play className="w-4 h-4" />
          {quiz.attempts > 0 ? "Retake Quiz" : "Start Quiz"}
        </button>
      );
    }

    if (
      quiz.status === "completed" ||
      quiz.attempts >= (quiz.maxAttempts || 1)
    ) {
      return (
        <button
          onClick={() =>
            navigate(
              `/student/elearning/quizzes/${quiz._id}/results/${quiz.lastAttempt?.id}`
            )
          }
          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-5 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 font-semibold"
        >
          <Eye className="w-4 h-4" />
          View Results
        </button>
      );
    }

    return (
      <button
        disabled
        className="bg-slate-300 text-white px-5 py-3 rounded-xl cursor-not-allowed flex items-center justify-center gap-2 font-semibold opacity-60"
      >
        <AlertCircle className="w-4 h-4" />
        Unavailable
      </button>
    );
  };

  const student = studentData?.data;

  // Prevent e-course students from accessing regular student pages
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            Regular quizzes are not available for e-course students.
          </p>
          <a
            href="/student/ecourse-dashboard"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to E-Learning Dashboard
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <ArrowLeft
          onClick={() => navigate("/student/elearning/dashboard")}
          className="max-w-fit sm:w-auto text-blue-500"
        />

        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
            My Quizzes
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-2">
            Take quizzes to test your knowledge and track your progress
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 sm:p-6 hover:shadow-lg transition-all duration-300">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search quizzes or courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-4 py-3 text-sm sm:text-base border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50/50 hover:bg-white transition-colors"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full sm:w-auto bg-slate-100/60 text-slate-700 px-6 py-3 rounded-xl hover:bg-slate-200 transition-all duration-300 flex items-center justify-center gap-2 font-medium"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full p-3 text-sm sm:text-base border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50/50 hover:bg-white transition-colors"
                >
                  <option value="">All Statuses</option>
                  <option value="available">Available</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="unavailable">Unavailable</option>
                  <option value="overdue">Overdue</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quiz Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 sm:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md flex-shrink-0">
              <Play className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900">
                {quizzes.filter((q: any) => q.status === "available").length}
              </p>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">
                Available
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 sm:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md flex-shrink-0">
              <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900">
                {quizzes.filter((q: any) => q.status === "completed").length}
              </p>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">
                Completed
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 sm:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-md flex-shrink-0">
              <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900">
                {Math.round(
                  quizzes
                    .filter((q: any) => q.bestScore)
                    .reduce(
                      (acc: number, q: any) =>
                        acc + (q.bestScore?.percentage || 0),
                      0
                    ) / quizzes.filter((q: any) => q.bestScore).length || 0
                )}
                %
              </p>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">
                Avg Score
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 sm:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center shadow-md flex-shrink-0">
              <AlertCircle className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900">
                {quizzes.filter((q: any) => q.status === "overdue").length}
              </p>
              <p className="text-xs sm:text-sm text-slate-600 font-medium">
                Overdue
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quizzes List */}
      <div className="space-y-3 sm:space-y-4">
        {filteredQuizzes.map((quiz) => (
          <div
            key={quiz._id}
            className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-xl transition-all duration-300 group"
          >
            <div className="p-4 sm:p-6">
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 break-words group-hover:text-blue-600 transition-colors">
                      {quiz.title}
                    </h3>
                    <span
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 w-fit flex-shrink-0 shadow-sm ${getStatusColor(
                        (quiz as any).status
                      )}`}
                    >
                      {getStatusIcon((quiz as any).status)}
                      {(quiz as any).status.charAt(0).toUpperCase() +
                        (quiz as any).status.slice(1).replace("_", " ")}
                    </span>
                  </div>
                  {(quiz as any).availabilityReason && (
                    <p className="text-xs text-orange-600 mb-2 break-words bg-orange-50 px-3 py-2 rounded-lg">
                      {(quiz as any).availabilityReason}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm sm:text-base font-semibold text-blue-600">
                      {typeof (quiz as any).courseId === "object"
                        ? (quiz as any).courseId?.title ||
                          (quiz as any).courseId?.name ||
                          "Unknown Course"
                        : "Unknown Course"}
                    </p>
                  </div>
                  <p className="text-sm sm:text-base text-slate-600 mb-4 break-words leading-relaxed">
                    {quiz.description}
                  </p>
                </div>
              </div>

              {/* Quiz Details */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-slate-600" />
                  </div>
                  <span className="font-medium">
                    {(quiz as any).timeLimit} min
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 text-slate-600" />
                  </div>
                  <span className="font-medium">
                    {(quiz as any).totalQuestions || 0} questions
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Award className="w-4 h-4 text-slate-600" />
                  </div>
                  <span className="font-medium">
                    {(quiz as any).totalPoints || 0} pts
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-4 h-4 text-slate-600" />
                  </div>
                  <span className="font-medium">
                    {(quiz as any).attempts}/{(quiz as any).maxAttempts || 1}{" "}
                    tries
                  </span>
                </div>
              </div>

              {/* Due Date */}
              {(quiz as any).dueDate && (
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 mb-4 bg-slate-50 px-3 py-2 rounded-lg">
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium">
                    {formatDueDate((quiz as any).dueDate)}
                  </span>
                </div>
              )}

              {/* Score Information */}
              {((quiz as any).bestScore || (quiz as any).lastAttempt) && (
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {(quiz as any).bestScore && (
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200/50">
                        <h4 className="text-sm sm:text-base font-bold text-green-900 mb-2 flex items-center gap-2">
                          <Award className="w-4 h-4" />
                          Best Score
                        </h4>
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className={`text-2xl sm:text-3xl font-bold ${getScoreColor(
                              (quiz as any).bestScore.percentage
                            )}`}
                          >
                            {(quiz as any).bestScore.percentage}%
                          </span>
                          <span className="text-xs sm:text-sm text-green-700 font-semibold">
                            {(quiz as any).bestScore.score}/
                            {(quiz as any).totalPoints} points
                          </span>
                        </div>
                        <p className="text-xs text-green-600 break-words">
                          Attempt {(quiz as any).bestScore.attemptNumber} •{" "}
                          {(quiz as any).bestScore.completedAt
                            ? new Date(
                                (quiz as any).bestScore.completedAt
                              ).toLocaleDateString()
                            : "In Progress"}
                        </p>
                      </div>
                    )}

                    {(quiz as any).lastAttempt &&
                      (quiz as any).lastAttempt !== (quiz as any).bestScore && (
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200/50">
                          <h4 className="text-sm sm:text-base font-bold text-blue-900 mb-2 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Last Attempt
                          </h4>
                          <div className="flex items-center justify-between mb-2">
                            <span
                              className={`text-2xl sm:text-3xl font-bold ${getScoreColor(
                                (quiz as any).lastAttempt.percentage
                              )}`}
                            >
                              {(quiz as any).lastAttempt.percentage}%
                            </span>
                            <span className="text-xs sm:text-sm text-blue-700 font-semibold">
                              {(quiz as any).lastAttempt.score}/
                              {(quiz as any).totalPoints} points
                            </span>
                          </div>
                          <p className="text-xs text-blue-600 break-words">
                            Attempt {(quiz as any).lastAttempt.attemptNumber} •{" "}
                            {new Date(
                              (quiz as any).lastAttempt.completedAt
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
                <button
                  onClick={() => {
                    const courseId =
                      typeof (quiz as any).courseId === "object"
                        ? (quiz as any).courseId?._id
                        : (quiz as any).courseId;
                    const courseType = (quiz as any).courseType;

                    if (courseId) {
                      if (courseType === "ecourse") {
                        navigate(`/student/elearning/course/${courseId}`);
                      } else {
                        // For regular courses, navigate to the courses list
                        navigate(`/student/courses`);
                      }
                    }
                  }}
                  disabled={!(quiz as any).courseId}
                  className={`w-full sm:w-auto px-5 py-3 rounded-xl flex items-center justify-center gap-2 font-medium transition-all duration-300 ${
                    (quiz as any).courseId
                      ? "bg-slate-100/60 text-slate-700 hover:bg-slate-200 hover:shadow-md"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  View Course
                </button>

                {getActionButton(quiz)}

                {(quiz as any).bestScore && (
                  <button
                    onClick={() =>
                      navigate(
                        `/student/elearning/quizzes/${quiz._id}/results/${
                          (quiz as any).bestScore?.id
                        }`
                      )
                    }
                    className="w-full sm:w-auto bg-slate-100/60 text-slate-700 px-5 py-3 rounded-xl hover:bg-slate-200 hover:shadow-md transition-all duration-300 flex items-center justify-center gap-2 font-medium"
                  >
                    <BarChart3 className="w-4 h-4" />
                    View Results
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredQuizzes.length === 0 && (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <HelpCircle className="w-10 h-10 text-slate-500" />
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">
            No quizzes found
          </h3>
          <p className="text-sm sm:text-base text-slate-600 mb-6 leading-relaxed">
            {searchTerm || statusFilter
              ? "Try adjusting your search criteria."
              : "You don't have any quizzes yet."}
          </p>
          {(searchTerm || statusFilter) && (
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("");
              }}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentQuizzes;
